<template>
  <div class="hc-compact-page appointment-detail-page">
    <PageHeader
      title="Detail Perjanjian"
      :subtitle="data.id ? `Ringkasan kunjungan dan data perjanjian #${data.id}` : 'Memuat data perjanjian'"
      icon="far fa-calendar-check"
    >
      <template #actions>
        <div class="appointment-detail-actions">
          <b-button variant="outline-secondary" @click="$router.push('/appointment/list/0')">
            <i class="fas fa-arrow-left"></i><span>Kembali</span>
          </b-button>
          <b-button v-if="data.id" variant="outline-primary" @click="btnPrintStickerOnClick(data)">
            <i class="fas fa-print"></i><span>Cetak Stiker</span>
          </b-button>
          <b-button
            v-if="data.id && manipulateBtn"
            variant="primary"
            @click="$router.push({ path: '/appointment/edit/' + data.id })"
          >
            <i class="far fa-edit"></i><span>Edit</span>
          </b-button>
        </div>
      </template>
    </PageHeader>

    <div v-if="loading" class="appointment-detail-loading">
      <b-spinner small variant="primary" />
      <span>Memuat detail perjanjian...</span>
    </div>

    <template v-else-if="data.id">
      <section class="appointment-summary-card">
        <div class="appointment-summary-card__patient">
          <div class="appointment-patient-avatar" aria-hidden="true">
            {{ patientInitials }}
          </div>
          <div class="appointment-summary-card__identity">
            <div class="appointment-summary-card__eyebrow">Pasien</div>
            <h2>{{ data.display_patient_name || data.patient_name || '-' }}</h2>
            <div class="appointment-summary-card__meta">
              <span><i class="far fa-id-card"></i>{{ data.patient_number || 'Nomor pasien belum tersedia' }}</span>
              <span v-if="data.patient_gender"><i class="fas fa-venus-mars"></i>{{ data.patient_gender }}</span>
              <span v-if="data.patient_birth_date"><i class="fas fa-birthday-cake"></i>{{ formatShortDate(data.patient_birth_date) }}</span>
            </div>
            <div class="appointment-summary-card__badges">
              <span class="appointment-badge" :class="statusBadgeClass">
                <i class="fas fa-circle"></i>{{ data.appointment_status_name || 'Status belum ditentukan' }}
              </span>
              <span class="appointment-badge appointment-badge--coverage">
                <i :class="isBpjs ? 'fas fa-shield-alt' : 'fas fa-wallet'"></i>
                {{ isBpjs ? 'BPJS/JKN' : 'Umum' }}
              </span>
            </div>
          </div>
        </div>

        <div class="appointment-summary-card__schedule">
          <div class="appointment-schedule-icon"><i class="far fa-calendar-alt"></i></div>
          <div>
            <span>Jadwal kunjungan</span>
            <strong>{{ formattedAppointmentDate }}</strong>
            <small><i class="far fa-clock"></i>{{ data.time || '-' }}</small>
          </div>
        </div>
      </section>

      <div class="appointment-detail-grid">
        <section class="appointment-info-card">
          <div class="appointment-info-card__header">
            <div class="appointment-info-card__icon"><i class="fas fa-user-md"></i></div>
            <div>
              <h3>Informasi Kunjungan</h3>
              <p>Dokter, tindakan, dan lokasi pelayanan</p>
            </div>
          </div>
          <div class="appointment-info-list">
            <DetailRow icon="fas fa-user-md" label="Dokter" :value="data.doctor_name" />
            <DetailRow icon="fas fa-hand-holding-medical" label="Jenis tindakan" :value="data.action_type_name" />
            <DetailRow icon="fas fa-door-open" label="Ruangan" :value="data.room_name" />
            <div class="appointment-info-row">
              <div class="appointment-info-row__icon"><i class="fas fa-video"></i></div>
              <div class="appointment-info-row__content">
                <span>Tautan meeting</span>
                <a
                  v-if="meetingLinkAvailable"
                  :href="data.live_meeting_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >Buka tautan meeting <i class="fas fa-external-link-alt"></i></a>
                <strong v-else>-</strong>
              </div>
            </div>
          </div>
        </section>

        <section class="appointment-info-card">
          <div class="appointment-info-card__header">
            <div class="appointment-info-card__icon appointment-info-card__icon--payment">
              <i class="fas fa-receipt"></i>
            </div>
            <div>
              <h3>Pembayaran</h3>
              <p>Metode dan status kepesertaan kunjungan</p>
            </div>
          </div>
          <div class="appointment-payment-summary" :class="{ 'appointment-payment-summary--bpjs': isBpjs }">
            <i :class="isBpjs ? 'fas fa-shield-alt' : 'fas fa-wallet'"></i>
            <div>
              <span>Cara pembayaran</span>
              <strong>{{ isBpjs ? 'BPJS/JKN' : 'Umum' }}</strong>
            </div>
          </div>
          <div class="appointment-info-list">
            <template v-if="isBpjs">
              <DetailRow icon="far fa-credit-card" label="Nomor kartu" :value="data.bpjs_card_number" />
              <DetailRow icon="fas fa-check-circle" label="Status peserta" :value="data.bpjs_participant_status" />
              <DetailRow icon="fas fa-clinic-medical" label="FKTP" :value="data.bpjs_fktp_name" />
            </template>
            <DetailRow v-else icon="fas fa-info-circle" label="Keterangan" value="Pembayaran mandiri/non-BPJS" />
          </div>
        </section>

        <section class="appointment-info-card appointment-info-card--wide">
          <div class="appointment-info-card__header">
            <div class="appointment-info-card__icon appointment-info-card__icon--notes"><i class="far fa-file-alt"></i></div>
            <div>
              <h3>Catatan Klinis Awal</h3>
              <p>Keluhan dan catatan pendukung perjanjian</p>
            </div>
          </div>
          <div class="appointment-notes-grid">
            <div class="appointment-note-box">
              <span>Keluhan awal</span>
              <p>{{ data.complaint || 'Belum ada keluhan awal.' }}</p>
            </div>
            <div class="appointment-note-box">
              <span>Catatan</span>
              <p>{{ data.notes || 'Belum ada catatan tambahan.' }}</p>
            </div>
          </div>
        </section>
      </div>

      <div v-if="manipulateBtn" class="appointment-danger-zone">
        <div>
          <strong>Hapus perjanjian</strong>
          <span>Data yang dihapus tidak akan tampil lagi pada daftar perjanjian.</span>
        </div>
        <b-button variant="outline-danger" @click="btnDeleteOnClick">
          <i class="far fa-trash-alt"></i> Hapus
        </b-button>
      </div>
    </template>

    <ModalFormSticker :data="print" :count="printCount" />
  </div>
</template>

<script>
import PageHeader from "@/component/general/PageHeader.vue";
import ModalFormSticker from "@/component/appointments/ModalFormStickerPrint.vue";
import module from "@/core/modules/CrudModule.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

const DetailRow = {
  functional: true,
  props: {
    icon: String,
    label: String,
    value: [String, Number],
  },
  render(createElement, context) {
    const props = context.props;
    return createElement("div", { class: "appointment-info-row" }, [
      createElement("div", { class: "appointment-info-row__icon" }, [
        createElement("i", { class: props.icon }),
      ]),
      createElement("div", { class: "appointment-info-row__content" }, [
        createElement("span", props.label),
        createElement("strong", props.value || "-"),
      ]),
    ]);
  },
};

export default {
  components: {
    PageHeader,
    ModalFormSticker,
    DetailRow,
  },

  data() {
    return {
      data: {},
      loading: true,
      print: {
        patient_number: "",
        patient_name: "",
        patient_gender: "",
        patient_birth_date: "",
        appointment_date: "",
      },
      printCount: 2,
      manipulateBtn: false,
    };
  },

  computed: {
    patientInitials() {
      const name = this.data.display_patient_name || this.data.patient_name || "P";
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
    isBpjs() {
      return this.data.coverage_type === "bpjs";
    },
    meetingLinkAvailable() {
      return /^https?:\/\//i.test(this.data.live_meeting_link || "");
    },
    formattedAppointmentDate() {
      return this.formatLongDate(this.data.date);
    },
    statusBadgeClass() {
      const status = String(this.data.appointment_status_name || "").toLowerCase();
      if (status.includes("batal")) return "appointment-badge--danger";
      if (status.includes("ditangani")) return "appointment-badge--info";
      if (status.includes("konfirmasi")) return "appointment-badge--success";
      if (status.includes("menunggu")) return "appointment-badge--warning";
      return "appointment-badge--primary";
    },
  },

  methods: {
    parseDate(value) {
      if (!value) return null;
      const parsed = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    formatLongDate(value) {
      const date = this.parseDate(value);
      if (!date) return "-";
      return new Intl.DateTimeFormat("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
    },
    formatShortDate(value) {
      const date = this.parseDate(value);
      if (!date) return "-";
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    },
    btnPrintStickerOnClick(data) {
      this.$bvModal.show("modal-form-sticker-print");
      this.print.patient_number = data.patient_number;
      this.print.patient_name = data.display_patient_name;
      this.print.patient_gender = data.patient_gender;
      this.print.patient_birth_date = data.patient_birth_date;
      this.print.appointment_date = data.date;
    },
    async btnDeleteOnClick() {
      const result = await module.delete("appointments/" + this.$route.params.id);
      if (result) this.$router.push("/appointment/list/0");
    },
    async get() {
      try {
        this.data = (await module.get("appointments/" + this.$route.params.id)) || {};
        if (!this.data.id) this.$router.push("/appointment/list/0");
      } finally {
        this.loading = false;
      }
    },
    setActiveMenu() {
      const accessRights = JSON.parse(
        window.localStorage.getItem("access_right_display") || "[]"
      );
      this.manipulateBtn = Array.isArray(accessRights)
        && accessRights.some((right) => String(right) === "5002");
    },
  },

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Pendaftaran atau Perjanjian", route: "/appointment/list/0" },
      { title: "Detail" },
    ]);
    this.get();
    this.setActiveMenu();
  },
};
</script>

<style lang="scss" scoped>
.appointment-detail-page {
  color: #1e293b;
}

.appointment-detail-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.appointment-detail-actions .btn {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0.55rem 1rem;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 700;
}

.appointment-detail-actions i {
  margin: 0 !important;
}

.appointment-detail-loading {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid #e7edf4;
  border-radius: 16px;
  background: #fff;
  color: #64748b;
  font-weight: 600;
}

.appointment-summary-card {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 14px;
  padding: 24px;
  overflow: hidden;
  border: 1px solid #dceae8;
  border-radius: 18px;
  background:
    radial-gradient(circle at 8% 15%, rgba(30, 149, 143, 0.1), transparent 31%),
    linear-gradient(135deg, #ffffff 0%, #f6fbfb 100%);
  box-shadow: 0 12px 30px rgba(20, 50, 99, 0.06);
}

.appointment-summary-card__patient {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 18px;
}

.appointment-patient-avatar {
  width: 76px;
  height: 76px;
  flex: 0 0 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 5px solid rgba(255, 255, 255, 0.85);
  border-radius: 22px;
  background: linear-gradient(135deg, #1e958f, #14608f);
  box-shadow: 0 10px 24px rgba(30, 149, 143, 0.22);
  color: #fff;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.appointment-summary-card__identity {
  min-width: 0;
}

.appointment-summary-card__eyebrow {
  margin-bottom: 3px;
  color: #1e958f;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.appointment-summary-card h2 {
  margin: 0 0 8px;
  color: #172033;
  font-size: 1.35rem;
  font-weight: 750;
  line-height: 1.3;
}

.appointment-summary-card__meta,
.appointment-summary-card__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px 14px;
}

.appointment-summary-card__meta {
  color: #64748b;
  font-size: 0.77rem;
  font-weight: 600;
}

.appointment-summary-card__meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.appointment-summary-card__meta i {
  color: #8aa0b5;
}

.appointment-summary-card__badges {
  margin-top: 12px;
}

.appointment-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eef4ff;
  color: #315db5;
  font-size: 0.7rem;
  font-weight: 800;
}

.appointment-badge .fa-circle {
  font-size: 6px;
}

.appointment-badge--success { background: #e8f8f0; color: #14764b; }
.appointment-badge--warning { background: #fff7df; color: #9a6500; }
.appointment-badge--danger { background: #ffeded; color: #b93a3a; }
.appointment-badge--info { background: #e8f5fb; color: #197293; }
.appointment-badge--coverage { background: #edf7f6; color: #157b76; }

.appointment-summary-card__schedule {
  min-width: 290px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 17px 19px;
  border: 1px solid #dceae8;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.86);
}

.appointment-schedule-icon {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(30, 149, 143, 0.1);
  color: #1e958f;
  font-size: 1.05rem;
}

.appointment-summary-card__schedule span,
.appointment-summary-card__schedule strong,
.appointment-summary-card__schedule small {
  display: block;
}

.appointment-summary-card__schedule span {
  margin-bottom: 3px;
  color: #8494a6;
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.appointment-summary-card__schedule strong {
  color: #1e293b;
  font-size: 0.9rem;
  line-height: 1.4;
}

.appointment-summary-card__schedule small {
  margin-top: 5px;
  color: #1e958f;
  font-size: 0.78rem;
  font-weight: 750;
}

.appointment-summary-card__schedule small i {
  margin-right: 6px;
}

.appointment-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.appointment-info-card {
  padding: 20px;
  border: 1px solid #e4ebf2;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(20, 50, 99, 0.045);
}

.appointment-info-card--wide {
  grid-column: 1 / -1;
}

.appointment-info-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 17px;
  padding-bottom: 14px;
  border-bottom: 1px solid #edf1f5;
}

.appointment-info-card__icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: #edf7f6;
  color: #1e958f;
}

.appointment-info-card__icon--payment { background: #eef4ff; color: #3c67ba; }
.appointment-info-card__icon--notes { background: #fff6e6; color: #b87512; }

.appointment-info-card__header h3 {
  margin: 0 0 2px;
  color: #1e293b;
  font-size: 0.92rem;
  font-weight: 800;
}

.appointment-info-card__header p {
  margin: 0;
  color: #8a99aa;
  font-size: 0.72rem;
}

.appointment-info-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px 18px;
}

::v-deep .appointment-info-row {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

::v-deep .appointment-info-row__icon {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f4f7fa;
  color: #71859a;
  font-size: 0.72rem;
}

::v-deep .appointment-info-row__content {
  min-width: 0;
}

::v-deep .appointment-info-row__content span,
::v-deep .appointment-info-row__content strong {
  display: block;
}

::v-deep .appointment-info-row__content span {
  margin-bottom: 2px;
  color: #8a99aa;
  font-size: 0.68rem;
  font-weight: 650;
}

::v-deep .appointment-info-row__content strong,
::v-deep .appointment-info-row__content a {
  overflow-wrap: anywhere;
  color: #334155;
  font-size: 0.79rem;
  font-weight: 750;
  line-height: 1.45;
}

::v-deep .appointment-info-row__content a {
  color: #1e958f;
}

.appointment-payment-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 13px 15px;
  border-radius: 12px;
  background: #f6f8fb;
  color: #52667c;
}

.appointment-payment-summary--bpjs {
  background: #edf8f3;
  color: #16805d;
}

.appointment-payment-summary > i {
  width: 34px;
  text-align: center;
  font-size: 1.15rem;
}

.appointment-payment-summary span,
.appointment-payment-summary strong {
  display: block;
}

.appointment-payment-summary span {
  color: #8190a1;
  font-size: 0.68rem;
  font-weight: 650;
}

.appointment-payment-summary strong {
  color: inherit;
  font-size: 0.87rem;
  font-weight: 800;
}

.appointment-notes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.appointment-note-box {
  min-height: 92px;
  padding: 14px 15px;
  border: 1px solid #e9eef3;
  border-radius: 12px;
  background: #fafcfd;
}

.appointment-note-box span {
  display: block;
  margin-bottom: 7px;
  color: #7c8da0;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.appointment-note-box p {
  margin: 0;
  color: #46576a;
  font-size: 0.8rem;
  line-height: 1.65;
  white-space: pre-wrap;
}

.appointment-danger-zone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 14px;
  padding: 14px 18px;
  border: 1px solid #f2dada;
  border-radius: 14px;
  background: #fffafa;
}

.appointment-danger-zone strong,
.appointment-danger-zone span {
  display: block;
}

.appointment-danger-zone strong {
  margin-bottom: 2px;
  color: #8e3333;
  font-size: 0.78rem;
}

.appointment-danger-zone span {
  color: #9b6c6c;
  font-size: 0.7rem;
}

.appointment-danger-zone .btn {
  border-radius: 9px;
  font-size: 0.75rem;
  font-weight: 750;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .appointment-summary-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .appointment-summary-card__schedule {
    width: 100%;
    min-width: 0;
  }

  .appointment-detail-grid {
    grid-template-columns: 1fr;
  }

  .appointment-info-card--wide {
    grid-column: auto;
  }
}

@media (max-width: 767.98px) {
  .appointment-detail-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .appointment-detail-actions .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .appointment-summary-card {
    padding: 18px;
  }

  .appointment-summary-card__patient {
    align-items: flex-start;
  }

  .appointment-patient-avatar {
    width: 58px;
    height: 58px;
    flex-basis: 58px;
    border-radius: 17px;
    font-size: 1rem;
  }

  .appointment-summary-card h2 {
    font-size: 1.08rem;
  }

  .appointment-info-list,
  .appointment-notes-grid {
    grid-template-columns: 1fr;
  }

  .appointment-danger-zone {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
